import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import type { WizardStepProps, ScheduleSlice } from '../../../engine/types';
import { TournamentFormat } from '@/types/tournament.model';
import { toDateInputFormat } from '@/utils/formatters';

// Padel-specific defaults
const PADEL_DEFAULT_MATCH_DURATION = 60; // 60 minutes for padel
const AMERICAN_MATCH_DURATION = 30;

/**
 * Configuration for the Padel ScheduleStep component.
 */
export interface PadelScheduleStepConfig {
  defaultMatchDuration?: number;
  minMatchDuration?: number;
  maxMatchDuration?: number;
  quickDurationOptions?: number[];
}

const DEFAULT_CONFIG: PadelScheduleStepConfig = {
  defaultMatchDuration: PADEL_DEFAULT_MATCH_DURATION,
  minMatchDuration: 45,
  maxMatchDuration: 120,
  quickDurationOptions: [45, 60, 75, 90],
};

interface PadelScheduleStepProps extends WizardStepProps<ScheduleSlice> {
  config?: PadelScheduleStepConfig;
}

export function ScheduleStep({
  data,
  updateData,
  draft,
  config: userConfig,
}: PadelScheduleStepProps) {
  const config = { ...DEFAULT_CONFIG, ...userConfig };
  const selectedClub = draft.clubSelection?.selectedClub;
  const isAmerican = draft.fixtureConfig?.format === TournamentFormat.AMERICAN;
  const effectiveMin = isAmerican ? AMERICAN_MATCH_DURATION : (config.minMatchDuration ?? 45);
  const quickOptions = isAmerican
    ? [AMERICAN_MATCH_DURATION, ...(config.quickDurationOptions ?? [45, 60, 75, 90]).filter((d) => d !== AMERICAN_MATCH_DURATION)]
    : (config.quickDurationOptions ?? [45, 60, 75, 90]);

  // Initialize with defaults if no data
  useEffect(() => {
    if (!data) {
      updateData({
        startDate: null,
        endDate: null,
        signupDeadlineDate: null,
        matchDuration: isAmerican
          ? AMERICAN_MATCH_DURATION
          : (config.defaultMatchDuration ?? PADEL_DEFAULT_MATCH_DURATION),
        schedule: [],
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, updateData]);

  // Handle signup deadline change
  const handleSignupDeadlineChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const date = new Date(dateValue + 'T00:00:00');
      updateData({ signupDeadlineDate: date });
    } else {
      updateData({ signupDeadlineDate: null });
    }
  };

  // Handle match duration change
  const handleMatchDurationChange = (value: number) => {
    const clampedValue = Math.max(
      effectiveMin,
      Math.min(value, config.maxMatchDuration ?? 180),
    );
    updateData({ matchDuration: clampedValue });
  };

  // Validate signup deadline
  const getSignupDeadlineError = (): string | null => {
    if (!data?.signupDeadlineDate || !data?.startDate) return null;

    const deadline = new Date(data.signupDeadlineDate);
    const startDate = new Date(data.startDate);

    deadline.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);

    if (deadline >= startDate) {
      return 'La fecha de cierre debe ser anterior al inicio del torneo';
    }

    return null;
  };

  const signupDeadlineError = getSignupDeadlineError();

  return (
    <div className="space-y-6">
      {/* Signup Deadline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" />
            Fecha de cierre de inscripción
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="signup-deadline">Fecha límite *</Label>
            <Input
              id="signup-deadline"
              type="date"
              value={
                data?.signupDeadlineDate
                  ? toDateInputFormat(data.signupDeadlineDate)
                  : ''
              }
              onChange={handleSignupDeadlineChange}
              min={new Date().toISOString().split('T')[0]}
            />
            <p className="text-xs text-gray-500">
              Los equipos podrán inscribirse hasta esta fecha
            </p>
            {signupDeadlineError && (
              <p className="text-xs text-red-500">{signupDeadlineError}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Match Duration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5" />
            Duración de partidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min={effectiveMin}
                max={config.maxMatchDuration}
                step={15}
                value={data?.matchDuration ?? config.defaultMatchDuration}
                onChange={(e) =>
                  handleMatchDurationChange(
                    parseInt(e.target.value) ||
                      (config.defaultMatchDuration ??
                        PADEL_DEFAULT_MATCH_DURATION),
                  )
                }
                className="w-24"
              />
              <span className="text-gray-600">minutos</span>
            </div>

            {/* Quick select buttons */}
            <div className="flex flex-wrap gap-2">
              {quickOptions.map(
                (duration) => (
                  <button
                    key={duration}
                    type="button"
                    onClick={() => handleMatchDurationChange(duration)}
                    className={`rounded-full px-3 py-1 text-sm transition-colors ${
                      data?.matchDuration === duration
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {duration} min
                  </button>
                ),
              )}
            </div>

            <p className="text-xs text-gray-500">
              Duración típica para partidos de pádel: 60-90 minutos
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" />
            Horarios del torneo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedClub ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h4 className="font-medium text-gray-700">
                  {selectedClub.name}
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  {selectedClub.courts?.length ?? 0} canchas disponibles
                </p>
              </div>

              {/* Schedule selector placeholder */}
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                <Calendar className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-4 text-sm text-gray-500">
                  Aquí se mostrará el selector de horarios.
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  (Componente ScheduleSelection existente)
                </p>
              </div>

              {/* Info about selected schedules */}
              {data?.schedule && data.schedule.length > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                  <AlertCircle className="h-4 w-4 text-green-600" />
                  <p className="text-sm text-green-700">
                    {data.schedule.reduce(
                      (total, s) => total + (s.schedule?.length ?? 0),
                      0,
                    )}{' '}
                    días con horarios configurados
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <AlertCircle className="h-8 w-8 text-amber-500" />
              <p className="mt-2 text-sm text-gray-600">
                Debes seleccionar un club primero
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ScheduleStep;
